import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Button, Textarea } from '../ui';
import { 
  Printer, 
  FileText, 
  Plus, 
  Trash2, 
  CheckSquare, 
  Sliders, 
  Check, 
  Utensils, 
  Users, 
  Calendar, 
  ShieldCheck,
  Share2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { DEFAULT_ENABLED_MODULES } from '../../constants';

interface SheetColumn {
  id: string;
  label: string;
}

interface SheetPreset {
  name: string;
  eventName: string;
  location: string;
  instructions: string;
  rowCount: number;
  columns: SheetColumn[];
}

const PRESET_TEMPLATES: SheetPreset[] = [
  {
    name: 'Volunteer Shifts',
    eventName: 'Annual Community Clean-Up & Tree Planting',
    location: 'City Park Pavilion • Zone 4',
    instructions: 'Please sign in upon arrival. Note your shift time and station. Check out with site coordinator before leaving.',
    rowCount: 18,
    columns: [
      { id: 'c1', label: 'Print Name' },
      { id: 'c2', label: 'Cell Phone' },
      { id: 'c3', label: 'Email Address' },
      { id: 'c4', label: 'Shift Time' },
      { id: 'c5', label: 'Assigned Station' },
      { id: 'c6', label: 'Signature' }
    ]
  },
  {
    name: 'Potluck & Food',
    eventName: 'Neighborhood Association Fall Potluck',
    location: 'Clubhouse Main Hall',
    instructions: 'Please specify the item you are bringing, category (Entree, Side, Dessert, Drink), and note any common allergens (nuts/gluten/dairy).',
    rowCount: 18,
    columns: [
      { id: 'c1', label: 'Name' },
      { id: 'c2', label: 'Dish / Item to Bring' },
      { id: 'c3', label: 'Category' },
      { id: 'c4', label: 'Servings / Qty' },
      { id: 'c5', label: 'Allergens (if any)' },
      { id: 'c6', label: 'Arrival Time' }
    ]
  },
  {
    name: 'Sports Snack Roster',
    eventName: 'Youth Soccer Snack & Refreshment Schedule',
    location: 'Field #3 & Home Bleachers',
    instructions: 'Please provide healthy snacks (e.g. orange slices, fruit snacks) and individual waters or 100% juice boxes for 14 players.',
    rowCount: 12,
    columns: [
      { id: 'c1', label: 'Game Date' },
      { id: 'c2', label: 'Opponent' },
      { id: 'c3', label: 'Parent / Family Name' },
      { id: 'c4', label: 'Player Name & #' },
      { id: 'c5', label: 'Snack & Drink Item' },
      { id: 'c6', label: 'Confirmed (✓)' }
    ]
  },
  {
    name: 'Parent Chaperone',
    eventName: 'High School Band Trip & Field Day',
    location: 'State Convention Center, Austin TX',
    instructions: 'All chaperones must have approved volunteer background clearance on file with the main office. Please wear volunteer lanyard.',
    rowCount: 14,
    columns: [
      { id: 'c1', label: 'Chaperone Name' },
      { id: 'c2', label: 'Student Name' },
      { id: 'c3', label: 'Cell Phone' },
      { id: 'c4', label: 'Assigned Bus #' },
      { id: 'c5', label: 'Clearance (Y/N)' },
      { id: 'c6', label: 'Signature' }
    ]
  },
  {
    name: 'Meeting Sign-In',
    eventName: 'Board of Directors & General Assembly',
    location: 'Civic Center Conference Room B',
    instructions: 'Required attendance log for quorum verification and meeting minutes record. Please print legibly.',
    rowCount: 20,
    columns: [
      { id: 'c1', label: 'Full Name' },
      { id: 'c2', label: 'Organization / Role' },
      { id: 'c3', label: 'Email Address' },
      { id: 'c4', label: 'Voting Member (Y/N)' },
      { id: 'c5', label: 'Signature' }
    ]
  }
];

export function SignUpSheetGenerator() {
  const navigate = useNavigate();
  const [eventName, setEventName] = useState('Annual Volunteer Cleanup');
  const [date, setDate] = useState('October 15, 2026');
  const [location, setLocation] = useState('City Park Pavilion');
  const [instructions, setInstructions] = useState('Please sign in upon arrival. Note your shift time and assigned zone.');
  const [rowCount, setRowCount] = useState(16);
  const [selectedPreset, setSelectedPreset] = useState<string>('Volunteer Shifts');
  
  const [columns, setColumns] = useState<SheetColumn[]>([
    { id: 'col_1', label: 'Print Name' },
    { id: 'col_2', label: 'Email Address' },
    { id: 'col_3', label: 'Phone Number' },
    { id: 'col_4', label: 'Shift Time' },
    { id: 'col_5', label: 'Assigned Station' },
    { id: 'col_6', label: 'Signature' }
  ]);

  const applyPreset = (preset: SheetPreset) => {
    setSelectedPreset(preset.name);
    setEventName(preset.eventName);
    setLocation(preset.location);
    setInstructions(preset.instructions);
    setRowCount(preset.rowCount);
    setColumns(preset.columns);
  };

  const handleAddColumn = () => {
    if (columns.length >= 8) return;
    setColumns([...columns, { id: `col_${Date.now()}`, label: 'New Column' }]);
  };

  const handleRemoveColumn = (id: string) => {
    if (columns.length <= 2) return;
    setColumns(columns.filter(col => col.id !== id));
  };

  const handleChangeColumn = (id: string, value: string) => {
    setColumns(columns.map(col => col.id === id ? { ...col, label: value } : col));
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="text-left font-sans w-full">
      {/* SCREEN UI - Hidden during print */}
      <div className="print:hidden space-y-6">
        
        {/* Top Header Card */}
        <div className="bg-[#FFD600] border-[4px] border-black text-black p-6 shadow-[6px_6px_0px_#000] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="inline-block bg-black text-white px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-widest mb-1">
              Printable Sign-Up Sheet Generator
            </div>
            <h2 className="text-2xl sm:text-3xl font-black font-display uppercase tracking-tight flex items-center gap-2">
              <CheckSquare className="w-7 h-7 text-black shrink-0" />
              Sign-Up & Sign-In Sheet Creator
            </h2>
            <p className="font-bold text-sm max-w-xl text-black/80 mt-1">
              Design customized, printable sign-up sheets for volunteer shifts, potlucks, sports snacks, chaperone rosters, and board meetings.
            </p>
          </div>

          <Button
            onClick={handlePrint}
            className="bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase text-xs tracking-wider px-6 py-3 border-[2px] border-black shadow-[3px_3px_0px_#000] rounded-none flex items-center gap-2 cursor-pointer shrink-0"
          >
            <Printer className="w-4 h-4" />
            Print / Save PDF
          </Button>
        </div>

        {/* Preset Selector */}
        <div className="border-[3px] border-black dark:border-white p-4 bg-white dark:bg-black shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
          <span className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 block mb-2">
            Instant Sheet Presets:
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {PRESET_TEMPLATES.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => applyPreset(p)}
                className={`p-2.5 text-left border-[2px] border-black dark:border-white font-bold text-xs transition-all cursor-pointer ${
                  selectedPreset === p.name
                    ? 'bg-[#FFD600] text-black shadow-[2px_2px_0px_#000]'
                    : 'bg-[#F5F5F0] dark:bg-slate-900 text-black dark:text-white hover:bg-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-black uppercase tracking-tight truncate">{p.name}</span>
                  {selectedPreset === p.name && <Check className="w-3.5 h-3.5 shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 2-Column Editor + Live Preview */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          
          {/* Controls Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border-[3px] border-black dark:border-white p-6 bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-4">
              <h3 className="text-sm font-black font-mono uppercase tracking-widest border-b-[2px] border-black dark:border-white pb-2 flex items-center gap-2">
                <Sliders className="w-4 h-4 text-[#0044FF]" />
                Sheet Content & Columns
              </h3>
              
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-widest">
                  Event / Project Name
                </label>
                <Input 
                  value={eventName} 
                  onChange={(e) => setEventName(e.target.value)} 
                  className="font-bold border-[2px] border-black dark:border-white rounded-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-widest">Date</label>
                  <Input 
                    value={date} 
                    onChange={(e) => setDate(e.target.value)} 
                    className="text-xs border-[2px] border-black dark:border-white rounded-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-widest">Location</label>
                  <Input 
                    value={location} 
                    onChange={(e) => setLocation(e.target.value)} 
                    className="text-xs border-[2px] border-black dark:border-white rounded-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-widest">Instructions / Notes</label>
                <Textarea 
                  rows={2} 
                  value={instructions} 
                  onChange={(e) => setInstructions(e.target.value)} 
                  className="text-xs border-[2px] border-black dark:border-white rounded-none"
                />
              </div>

              <div className="pt-2 border-t-[2px] border-black dark:border-white">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Row Count (Lines)</label>
                  <span className="font-mono text-xs font-black">{rowCount} rows</span>
                </div>
                <input 
                  type="range" 
                  min="8" 
                  max="28" 
                  value={rowCount} 
                  onChange={(e) => setRowCount(parseInt(e.target.value))}
                  className="w-full cursor-pointer accent-[#0044FF]" 
                />
              </div>

              {/* Column Config */}
              <div className="pt-2 border-t-[2px] border-black dark:border-white space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Columns ({columns.length}/8)</label>
                  <Button 
                    onClick={handleAddColumn} 
                    disabled={columns.length >= 8}
                    variant="ghost" 
                    className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#0044FF] hover:bg-blue-50 dark:hover:bg-blue-950/20 p-1 h-auto"
                  >
                    <Plus size={12} className="mr-1 inline" /> Add Column
                  </Button>
                </div>
                
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {columns.map((col, idx) => (
                    <div key={col.id} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-black text-slate-400 w-4">{idx + 1}.</span>
                      <Input 
                        value={col.label} 
                        onChange={(e) => handleChangeColumn(col.id, e.target.value)}
                        className="h-8 text-xs border-[2px] border-black dark:border-white rounded-none"
                      />
                      <button 
                        onClick={() => handleRemoveColumn(col.id)}
                        disabled={columns.length <= 2}
                        className="text-slate-400 hover:text-red-500 disabled:opacity-30 p-1"
                        aria-label="Remove column"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t-[2px] border-black dark:border-white space-y-2">
                <Button 
                  onClick={() => {
                    const orgName = localStorage.getItem('orgsets_lead_org') || eventName || 'Volunteer Team';
                    const slug = orgName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `volunteers-${Date.now().toString(36)}`;
                    const pendingData = {
                      name: orgName,
                      slug,
                      template: 'CLUB',
                      tier: 'LOCAL',
                      visibility: 'PUBLIC',
                      modules: DEFAULT_ENABLED_MODULES,
                      sourceTool: 'sign-up-sheet-generator'
                    };
                    localStorage.setItem('pending_hub_creation', JSON.stringify(pendingData));
                    localStorage.setItem('pending_tool_org_draft', JSON.stringify({
                      toolSlug: 'sign-up-sheet-generator',
                      toolTitle: 'Free Online Sign-Up Sheet Generator',
                      orgName,
                      eventName,
                      date,
                      location,
                      rowCount,
                      timestamp: new Date().toISOString()
                    }));
                    const leadEmail = localStorage.getItem('orgsets_lead_email');
                    navigate(leadEmail 
                      ? `/sign-up?redirect_url=/dashboard&source=tool&slug=sign-up-sheet-generator&email=${encodeURIComponent(leadEmail)}`
                      : `/sign-up?redirect_url=/dashboard&source=tool&slug=sign-up-sheet-generator`);
                  }}
                  className="w-full bg-[#FFD600] hover:bg-[#E6C200] text-black font-black uppercase tracking-widest py-3 border-[2px] border-black shadow-[4px_4px_0px_#000] rounded-none cursor-pointer flex items-center justify-center gap-2"
                >
                  <Share2 size={16} />
                  Launch Live Digital Sign-Up Portal
                </Button>
                <Button 
                  onClick={handlePrint}
                  variant="secondary"
                  className="w-full font-black uppercase tracking-widest py-3 border-[2px] border-black shadow-[4px_4px_0px_#000] rounded-none cursor-pointer flex items-center justify-center gap-2 bg-white text-black hover:bg-gray-100"
                >
                  <Printer size={16} />
                  Print / Save PDF
                </Button>
              </div>

            </div>
          </div>

          {/* Live Preview Column */}
          <div className="lg:col-span-7 space-y-4">
            <span className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 block">
              Live Printable Sheet Preview (Letter Portrait):
            </span>

            <div className="border-[4px] border-black dark:border-white bg-slate-200 dark:bg-slate-950 p-4 sm:p-8 overflow-x-auto flex justify-center shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
              <div 
                className="origin-top scale-[0.55] sm:scale-[0.7] md:scale-[0.8] xl:scale-[0.9] transition-transform"
                style={{ width: '816px', minHeight: '1056px' }}
              >
                <SignUpSheetPaper
                  eventName={eventName}
                  date={date}
                  location={location}
                  instructions={instructions}
                  columns={columns}
                  rowCount={rowCount}
                />
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* PRINT-ONLY VIEW */}
      <div className="hidden print:block fixed inset-0 m-0 p-0 bg-white z-[99999]">
        <style type="text/css" media="print">
          {`
            @page {
              size: portrait;
              margin: 10mm;
            }
            body {
              margin: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background-color: white !important;
            }
          `}
        </style>
        <div className="w-full h-full p-4 bg-white text-black">
          <SignUpSheetPaper
            eventName={eventName}
            date={date}
            location={location}
            instructions={instructions}
            columns={columns}
            rowCount={rowCount}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Rendered Sheet Paper Component ───────────────────────────────────────────

interface PaperProps {
  eventName: string;
  date: string;
  location: string;
  instructions: string;
  columns: SheetColumn[];
  rowCount: number;
}

function SignUpSheetPaper({
  eventName,
  date,
  location,
  instructions,
  columns,
  rowCount
}: PaperProps) {
  return (
    <div className="w-full bg-white text-black p-10 border-2 border-black font-sans box-border shadow-xl min-h-[1000px] flex flex-col justify-between">
      {/* Sheet Header */}
      <div>
        <div className="border-b-[4px] border-black pb-4 mb-4 flex justify-between items-end">
          <div>
            <span className="text-[10px] font-mono font-black uppercase tracking-widest text-slate-500 block">
              OFFICIAL SIGN-UP ROSTER
            </span>
            <h1 className="text-3xl font-black font-display uppercase tracking-tight text-black mt-1">
              {eventName || 'Event Sign-Up'}
            </h1>
          </div>
          <div className="text-right font-mono text-xs font-bold space-y-0.5">
            {date && <p><span className="text-slate-500">DATE:</span> {date}</p>}
            {location && <p><span className="text-slate-500">LOCATION:</span> {location}</p>}
          </div>
        </div>

        {instructions && (
          <div className="bg-[#F5F5F0] border-l-[4px] border-[#0044FF] p-3 mb-6 text-xs text-slate-800 font-sans leading-relaxed">
            <span className="font-black uppercase tracking-wider text-[10px] block text-[#0044FF] mb-0.5">Instructions:</span>
            {instructions}
          </div>
        )}

        {/* Roster Table */}
        <table className="w-full text-left border-collapse border-[2px] border-black text-xs font-sans">
          <thead>
            <tr className="bg-slate-100 border-b-[2px] border-black text-black">
              <th className="border-r-[2px] border-black p-2.5 w-10 text-center font-mono font-black">#</th>
              {columns.map((col) => (
                <th key={col.id} className="border-r-[2px] border-black p-2.5 font-bold uppercase tracking-wider text-[11px] last:border-r-0">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }).map((_, i) => (
              <tr key={i} className="border-b border-black/30 last:border-b-0 h-10">
                <td className="border-r-[2px] border-black p-2 text-center font-mono font-bold text-slate-400 bg-slate-50/50">
                  {i + 1}
                </td>
                {columns.map((col) => (
                  <td key={col.id} className="border-r-[2px] border-black p-2 last:border-r-0"></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Notes & Page Indicator */}
      <div className="pt-6 border-t border-slate-300 flex justify-between items-center text-[9px] font-mono text-slate-500 mt-6">
        <span>GENERATED VIA ORGSETS.COM COMMUNITY TOOLBOX</span>
        <span>PAGE 1 OF 1</span>
        <span>CONFIDENTIAL / ROSTER RECORD</span>
      </div>
    </div>
  );
}
